import { fetchAPI } from "./utils";

// Helper function to get auth token from localStorage
const getAuthToken = (): string | null => {
    return localStorage.getItem("token");
};

export interface AudienceProfile {
    id: string;
    createdAt: string;
    updatedAt: string;
    name: string;
    description: string;
    companyId: string;
}

export interface CreateAudienceProfileRequest {
    name: string;
    description: string;
}

export interface UpdateAudienceProfileRequest {
    audienceProfileId: string;
    name?: string;
    description?: string;
}

export interface GenerateDescriptionResponse {
    description: string;
    quota: {
        limit: number;
        remaining: number;
    };
}

/**
 * Create a new AudienceProfile
 */
export const createAudienceProfile = async (data: CreateAudienceProfileRequest): Promise<AudienceProfile> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }
    return fetchAPI<AudienceProfile>("/create-audience-profile", "POST", data, token);
};

/**
 * Get all AudienceProfiles for the company, or a specific one by ID
 */
export const getAudienceProfiles = async (audienceProfileId?: string): Promise<AudienceProfile[]> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }
    const endpoint = audienceProfileId
        ? `/get-audience-profiles?audienceProfileId=${audienceProfileId}`
        : "/get-audience-profiles";
    const response = await fetchAPI<AudienceProfile | AudienceProfile[]>(endpoint, "GET", undefined, token);
    return Array.isArray(response) ? response : [response];
};

/**
 * Get a single AudienceProfile by ID
 */
export const getAudienceProfile = async (audienceProfileId: string): Promise<AudienceProfile> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }
    const endpoint = `/get-audience-profiles?audienceProfileId=${audienceProfileId}`;
    const response = await fetchAPI<AudienceProfile | AudienceProfile[]>(endpoint, "GET", undefined, token);
    return Array.isArray(response) ? response[0] : response;
};

/**
 * Update an AudienceProfile
 */
export const updateAudienceProfile = async (data: UpdateAudienceProfileRequest): Promise<AudienceProfile> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }
    return fetchAPI<AudienceProfile>("/update-audience-profile", "POST", data, token);
};

/**
 * Delete an AudienceProfile
 */
export const deleteAudienceProfile = async (audienceProfileId: string): Promise<{ message: string }> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }
    return fetchAPI<{ message: string }>("/delete-audience-profile", "POST", { audienceProfileId }, token);
};

/**
 * Generate an AI-assisted audience profile description
 */
export const generateAudienceDescription = async (
    name: string,
    partialDescription: string,
    browserLanguage: string,
): Promise<GenerateDescriptionResponse> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }
    return fetchAPI<GenerateDescriptionResponse>(
        "/generate-audience-description",
        "POST",
        { name, partialDescription, browserLanguage },
        token,
    );
};
